import type {
  CoreAssistantMessage,
  CoreToolMessage,
  Message,
  ToolInvocation,
} from 'ai';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { z } from 'zod';
import type { Source } from '@/components/sources-used';
import type { Message as DBMessage, Document } from '@repo/db';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(date: string | Date) {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

interface ApplicationError extends Error {
  info: string;
  status: number;
}

export const fetcher = async (url: string) => {
  const res = await fetch(url);

  if (!res.ok) {
    const error = new Error(
      'An error occurred while fetching the data.',
    ) as ApplicationError;

    error.info = await res.json();
    error.status = res.status;

    throw error;
  }

  return res.json();
};

export function getLocalStorage(key: string) {
  if (typeof window !== 'undefined') {
    return JSON.parse(localStorage.getItem(key) || '[]');
  }
  return [];
}

export function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function addToolMessageToChat({
  toolMessage,
  messages,
}: {
  toolMessage: CoreToolMessage;
  messages: Array<Message>;
}): Array<Message> {
  return messages.map((message) => {
    if (message.toolInvocations) {
      return {
        ...message,
        toolInvocations: message.toolInvocations.map((toolInvocation) => {
          const toolResult = toolMessage.content.find(
            (tool) => tool.toolCallId === toolInvocation.toolCallId,
          );

          if (toolResult) {
            return {
              ...toolInvocation,
              state: 'result',
              result: toolResult.result,
            };
          }

          return toolInvocation;
        }),
      };
    }

    return message;
  });
}

export function convertToUIMessages(
  messages: Array<DBMessage>,
): Array<Message> {
  return messages.reduce((chatMessages: Array<Message>, message) => {
    if (message.role === 'tool') {
      return addToolMessageToChat({
        toolMessage: message as CoreToolMessage,
        messages: chatMessages,
      });
    }

    let textContent = '';
    let reasoning: string | undefined = undefined;
    const toolInvocations: Array<ToolInvocation> = [];

    if (typeof message.content === 'string') {
      textContent = message.content;
    } else if (Array.isArray(message.content)) {
      for (const content of message.content) {
        if (content.type === 'text') {
          textContent += content.text;
        } else if (content.type === 'tool-call') {
          toolInvocations.push({
            state: 'call',
            toolCallId: content.toolCallId,
            toolName: content.toolName,
            args: content.args,
          });
        } else if (content.type === 'reasoning') {
          reasoning = content.reasoning;
        }
      }
    }

    chatMessages.push({
      id: message.id,
      role: message.role as Message['role'],
      content: textContent,
      reasoning,
      toolInvocations,
    });

    return chatMessages;
  }, []);
}

type ResponseMessageWithoutId = CoreToolMessage | CoreAssistantMessage;
type ResponseMessage = ResponseMessageWithoutId & { id: string };

export function sanitizeResponseMessages({
  messages,
  reasoning,
}: {
  messages: Array<ResponseMessage>;
  reasoning: string | undefined;
}) {
  const toolResultIds: Array<string> = [];

  for (const message of messages) {
    if (message.role === 'tool') {
      for (const content of message.content) {
        if (content.type === 'tool-result') {
          toolResultIds.push(content.toolCallId);
        }
      }
    }
  }

  const messagesBySanitizedContent = messages.map((message) => {
    if (message.role !== 'assistant') return message;

    if (typeof message.content === 'string') return message;

    const sanitizedContent = message.content.filter((content) =>
      content.type === 'tool-call'
        ? toolResultIds.includes(content.toolCallId)
        : content.type === 'text'
          ? content.text.length > 0
          : true,
    );

    if (reasoning) {
      // @ts-expect-error: reasoning message parts in sdk is wip
      sanitizedContent.push({ type: 'reasoning', reasoning });
    }

    return {
      ...message,
      content: sanitizedContent,
    };
  });

  return messagesBySanitizedContent.filter(
    (message) => message.content.length > 0,
  );
}

export function sanitizeUIMessages(messages: Array<Message>): Array<Message> {
  const messagesBySanitizedToolInvocations = messages.map((message) => {
    if (message.role !== 'assistant') return message;

    if (!message.toolInvocations) return message;

    const toolResultIds: Array<string> = [];

    for (const toolInvocation of message.toolInvocations) {
      if (toolInvocation.state === 'result') {
        toolResultIds.push(toolInvocation.toolCallId);
      }
    }

    const sanitizedToolInvocations = message.toolInvocations.filter(
      (toolInvocation) =>
        toolInvocation.state === 'result' ||
        toolResultIds.includes(toolInvocation.toolCallId),
    );

    return {
      ...message,
      toolInvocations: sanitizedToolInvocations,
    };
  });

  return messagesBySanitizedToolInvocations.filter(
    (message) =>
      message.content.length > 0 ||
      (message.toolInvocations && message.toolInvocations.length > 0),
  );
}

export function getMostRecentUserMessage(messages: Array<Message>) {
  const userMessages = messages.filter((message) => message.role === 'user');
  return userMessages.at(-1);
}

export function getDocumentTimestampByIndex(
  documents: Array<Document>,
  index: number,
) {
  if (!documents) return new Date();
  if (index > documents.length) return new Date();

  return documents[index].createdAt;
}

// Schema for validating source objects
const sourceSchema = z.object({
  type: z.enum(['internal', 'external']).default('external'),
  filename: z.string().optional(),
  fileNameAndPath: z.string().optional(),
  webUrl: z.string().optional(),
  fileType: z.string().optional(),
  relevance: z.number().min(0).max(100).optional(),
  context: z.string().optional(),
  keyPoints: z.array(z.string()).optional(),
});

const sourcesArraySchema = z.array(sourceSchema);

export function extractSourcesFromContent(content: string): Source[] | null {
  try {
    // Find JSON block in the content
    const jsonMatch = content.match(/```json\n([\s\S]*?)\n```/);
    if (!jsonMatch) return null;

    let jsonData: unknown;
    try {
      jsonData = JSON.parse(jsonMatch[1]);
    } catch (parseError) {
      // console.error('Error parsing sources JSON:', parseError); // Optional: too noisy?
      return null; // Parsing failed, likely invalid JSON syntax
    }

    // Handle explicit null case silently
    if (jsonData === null) {
      return null; // Treat null JSON as no sources found
    }

    // Try validating as an array
    const arrayValidationResult = sourcesArraySchema.safeParse(jsonData);
    if (arrayValidationResult.success) {
      return arrayValidationResult.data; // This handles empty arrays correctly
    }

    // If array validation fails, check if it's a single object
    if (
      typeof jsonData === 'object' && // jsonData !== null is implied here
      !Array.isArray(jsonData)
    ) {
      // Log the actual object content for debugging
      console.debug('Source object content:', JSON.stringify(jsonData));

      const singleObjectValidation = sourceSchema.safeParse(jsonData);
      if (singleObjectValidation.success) {
        // If single object is valid, wrap it in an array
        return [singleObjectValidation.data];
      } else {
        // If single object validation also fails, log *that* specific error
        console.error(
          'Invalid single source object JSON schema:',
          singleObjectValidation.error,
        );
        return null;
      }
    }

    // If it wasn't null, a valid array, or a valid single object,
    // it must be an invalid array or a primitive type other than null.
    // Log the array validation error for debugging.
    console.error(
      'Invalid sources JSON schema (expected array, or invalid array items/primitive):',
      arrayValidationResult.error,
    );
    return null;
  } catch (error) {
    // Catch unexpected errors during the process
    console.error('Error extracting sources from content:', error);
    return null;
  }
}

// Clean content by removing the JSON block
export function cleanContentFromSources(content: string): string {
  return content
    .replace(/```json\n[\s\S]*?\n```/, '') // Remove JSON code blocks
    .replace(/<source[^>]*>/g, '') // Remove opening source tags like <source type="internal">
    .replace(/<internal[^>]*>/g, '') // Remove opening internal tags like <internal>
    .replace(/<\/[^>]*>/g, '') // Remove closing tags like </internal>, </source>
    .trim();
}
